import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { watchAuthState, signOut } from '../lib/firebase'
import { useTheme, toggleTheme } from '../lib/theme'
import { meApi } from '../api/endpoints'
import { formatMemberSince } from '../lib/format'
import { AccountMenu } from './Header'

type Role = 'admin' | 'supplier' | 'buyer' | null

/**
 * SiteHeader — the ONE header, shared by the marketing homepage AND every
 * desktop app route (via AppLayout). One frosted bar so the chrome never
 * changes character page to page. Below 768px the app renders the mobile
 * shell instead, so this is desktop chrome only.
 *
 * Palettes (one component):
 *   • `overlay` (homepage hero, over the DARK video) — fixed, dark-glass
 *     scrim + light text, regardless of theme.
 *   • sticky (app pages) — light bar + dark text in light mode, dark bar +
 *     light text in dark mode (the navbar follows the theme toggle).
 * Backgrounds are inline rgba (never a Tailwind `/opacity` on a custom token
 * — that silently drops in v4).
 *
 * Nav is role-aware and dead-end-free:
 *   Browse → /search · Gemma → /chat  (both for EVERYONE)
 *   Sell → /sell (hidden for suppliers) · About → /about (signed-out)
 * Signed-in users get the full account dropdown (My shares, Favorite
 * suppliers, KYC, role surfaces, Aska+, devices, sign out) — the same
 * AccountMenu the app has always used.
 */
function useIdentity() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [role, setRole] = useState<Role>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [memberSince, setMemberSince] = useState<string | null>(null)
  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        setSignedIn(!!user)
        setEmail(user?.email ?? null)
        if (!user) { setRole(null); setMemberSince(null); return }
        try {
          const tok = await user.getIdTokenResult()
          const r = (tok.claims as { role?: string }).role
          setRole(r === 'admin' || r === 'supplier' ? r : 'buyer')
        } catch { setRole('buyer') }
        meApi.get()
          .then((r) => setMemberSince(formatMemberSince(r.user?.createdAt)))
          .catch(() => setMemberSince(null))
      })
    } catch { setSignedIn(false) }
    return () => unsub?.()
  }, [])
  return { signedIn, role, email, memberSince }
}

function ThemeToggle({ light }: { light: boolean }) {
  const dark = useTheme() === 'dark'
  return (
    <button
      type="button"
      onClick={() => toggleTheme()}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
      data-testid="site-theme-toggle"
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-default ${
        light ? 'text-white/70 hover:text-white' : 'text-ink/60 hover:text-gold'
      }`}
    >
      {dark ? (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4.2" /><path d="M12 2v2.5M12 19.5V22M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M2 12h2.5M19.5 12H22M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8" /></svg>
      ) : (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.3 6.3 0 0 0 10.5 10.5Z" /></svg>
      )}
    </button>
  )
}

export function SiteHeader({ overlay = false }: { overlay?: boolean }) {
  const navigate = useNavigate()
  const { signedIn, role, email, memberSince } = useIdentity()
  const dark = useTheme() === 'dark'

  // The top bar is a dark chrome bar in EVERY mode now: a deep-navy frosted bar
  // in the LIGHT app so it pops off the pale page (Tej 2026-10-22), the existing
  // tint in dark mode, the hero gradient when overlaying the landing video. The
  // chrome text is white throughout, so the white-text flag is always on.
  const light = true
  const link = light
    ? 'text-[14.5px] font-medium text-white/75 hover:text-white transition-colors duration-default'
    : 'text-[14.5px] font-medium text-ink/70 hover:text-gold transition-colors duration-default'

  const background = overlay
    ? 'linear-gradient(to bottom, rgba(6,17,31,0.62) 0%, rgba(6,17,31,0.20) 100%)'
    : dark
      ? 'rgba(14,16,20,0.85)'
      : 'rgba(12,26,52,0.92)'   // LIGHT app: deep-navy frosted bar so the header pops off the cream page

  return (
    <header
      className={`${overlay ? 'fixed' : 'sticky border-b'} ${light ? 'border-white/10' : 'border-line'} top-0 inset-x-0 z-40 backdrop-blur-xl`}
      style={{ background }}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-baseline gap-2" aria-label="DiaEx — home">
          <span className={`font-serif text-[24px] font-bold tracking-tight ${light ? 'text-white' : 'text-ink'}`}>DiaEx</span>
          <span className={`hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.22em] ${light ? 'text-white/55' : 'text-navy-500'}`}>by Aska</span>
        </Link>

        <nav className="flex items-center gap-5 sm:gap-7">
          <button type="button" onClick={() => navigate('/search')} className={link}>Browse</button>
          <button type="button" onClick={() => navigate('/chat')} className={link}>Gemma</button>
          {role !== 'supplier' && (
            <button type="button" onClick={() => navigate('/sell')} className={link}>Sell</button>
          )}
          {signedIn === false && (
            <button type="button" onClick={() => navigate('/about')} className={`hidden sm:inline ${link}`}>About</button>
          )}
          <ThemeToggle light={light} />
          {signedIn ? (
            <AccountMenu
              email={email}
              role={role}
              memberSince={memberSince}
              onSignOut={async () => { await signOut(); navigate('/') }}
            />
          ) : signedIn === false ? (
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className={`inline-flex h-9 items-center justify-center rounded-full px-4 text-[13.5px] font-semibold transition-colors duration-default ${
                light
                  ? 'border border-white/30 bg-white/15 text-white hover:bg-white/25 backdrop-blur'
                  : 'bg-navy text-white hover:bg-[#0a1a30]'
              }`}
              data-testid="site-signin"
            >
              Sign in
            </button>
          ) : null}
        </nav>
      </div>
    </header>
  )
}
